import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRightLong, faCheckCircle, faClock } from '@fortawesome/free-solid-svg-icons';
import { API_BASE } from './apiConfig';
import { Helmet } from 'react-helmet-async';
import FAQ from './FAQ'; 
import './Home.css';

const Home = () => {
  const { t } = useTranslation();
  const [categories, setCategories] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    const headers = {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };

    fetch(`${API_BASE}/api/categories`, { headers })
      .then(res => res.json())
      .then(setCategories)
      .catch(err => console.error("Error:", err));
  }, []);

  // Ծառայությունները վերցնում ենք թարգմանության ֆայլից
  const serviceHighlights = t('hero_highlights', { returnObjects: true });

  return (
    <div className="liber-work-wrapper">
      <Helmet>
        <title>{t('seo_title')}</title>
        <meta name="description" content={t('seo_description')} />
        <meta name="keywords" content={t('seo_keywords')} />
        <link rel="canonical" href="https://liber.am/" />

        <meta property="og:title" content={t('og_title')} />
        <meta property="og:description" content={t('og_description')} />
      </Helmet>

      {/* --- HERO SECTION --- */}
      <section className="hero-corporate">
        <div className="hero-left-brand">
          <motion.div 
            className="hero-brand-content"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="corporate-badge">
              <span className="live-dot"></span> {t('hero_badge')}
            </div>
            
            <h1 className="corporate-title">
              LIBER <br />
              <span className="gold-shimmer">GROUP</span>
            </h1>
            
            <div className="corporate-line"></div>
            
            <div className="tech-stack-indicator">
              <div className="step-dots">
                {[1,2,3,4,5,6,7,8].map(i => <span key={i} className="dot"></span>)}
              </div>
              <p>{t('auto_algorithm')}</p>
            </div>

            <ul className="corporate-services-grid">
              {Array.isArray(serviceHighlights) && serviceHighlights.map((service, index) => (
                <li key={index}>
                  <FontAwesomeIcon icon={faCheckCircle} className="service-icon" />
                  {service}
                </li>
              ))}
            </ul>
            
            <div className="hero-info-note">
              <FontAwesomeIcon icon={faClock} className="note-icon" />
              <span>{t('working_hours')}</span>
            </div>
          </motion.div>
        </div>

        <div className="hero-right-image">
        <img 
    // Օգտագործում ենք քո API_BASE-ը և նոր հասցեն
    src={`${API_BASE}/uploads/homescren.webp`} 
    alt="Liber Tech Logistics" 
    className="main-hero-img"
    // Ավելացրու սա, որ սկզբից սպիտակ չլինի մինչև բեռնվելը
    style={{ backgroundColor: '#1a1f36' }} 
  />
          <div className="img-overlay-dark"></div>

          <motion.div 
            className="booking-glass-info"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <span className="b-tag">{t('ecosystem_tag')}</span>
            <h3>{t('booking_title_1')} <br/> {t('booking_title_2')}</h3>
            <div className="b-divider"></div>
            
            <div className="b-steps">
              <div className="b-step-item">
                <span className="step-label">{t('step_1_label')}</span>
                <p>{t('step_1_desc')}</p>
              </div>
              <div className="b-step-item">
                <span className="step-label">{t('step_2_label')}</span>
                <p>{t('step_2_desc')}</p>
              </div>
            </div>

            <div className="b-footer-note">
              * {t('automated_note')}
            </div>
          </motion.div>
        </div>
      </section>

      {/* --- SERVICES GRID --- */}
      <section className="work-categories">
        <div className="work-section-title">
          <div className="accent-bar"></div>
          <h2>{t('home_services_title')}</h2>
        </div>

        <div className="work-grid">
          {categories.map((cat, i) => (
            <motion.div 
              key={cat.id || i} 
              className="work-card"
              whileInView={{ opacity: 1, y: 0 }}
              initial={{ opacity: 0, y: 30 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              onClick={() => navigate(`/service/${(cat.slug || cat.id).toString().toLowerCase()}`)}
            >
              <div className="work-card-img">
                <img 
                  src={cat.image ? `${API_BASE}${cat.image}` : '/placeholder.png'} 
                  alt={cat.name}
                  loading="lazy" 
                />
                <div className="work-overlay"><span>0{i + 1}</span></div>
              </div>
              <div className="work-card-content">
                <h3>{cat.name}</h3>
                <p>{t('professional_service')}</p>
                <div className="work-btn-min">
                  {t('home_see_more')} <FontAwesomeIcon icon={faArrowRightLong} />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="home-faq-wrapper">
        <FAQ />
      </section>
    </div>
  );
};

export default Home;